import {
  AXIS_LABEL_NUMBER,
  formatCurrency,
  formatNumber,
  formatPercent,
} from '@shared/formatters';
import {
  DataZoomComponentOption,
  EChartsInitOpts,
  EChartsOption,
} from 'echarts';
import { buildSeriesTooltip } from './tooltip-builder';

/**
 * Tooltip formatter "auto-from-series" — wrapper de retrocompat sobre
 * `buildSeriesTooltip` (em `./tooltip-builder`). Filtra séries com valor
 * `0`/`null`/`NaN` e formata cada valor visível com o `valueFormatter`
 * informado. Usado por templates (BarChart/PieChart/LineChart) no caminho
 * default, quando as métricas vêm das séries do ECharts em runtime.
 *
 * Para novos charts com métricas declaradas estaticamente, prefira
 * `buildTooltip(spec, context)` direto do `./tooltip-builder`.
 */
export function createTooltipFormatter(valueFormatter: (v: unknown) => string) {
  const tooltip = buildSeriesTooltip({ valueFormatter });
  return (tooltip as { formatter: (params: unknown) => string }).formatter;
}

/** @deprecated prefer formatNumber(value, 2) from shared/formatters */
export const formatter2decimal = {
  format: (value: any) => formatNumber(value, 2),
};

/** @deprecated prefer formatCurrency(value) from shared/formatters */
export const formatterCurrency = {
  format: (value: any) => formatCurrency(value),
};

export const DEFAULT_INITOPTS: EChartsInitOpts = {
  renderer: 'svg',
};

export const DEFAULT_CHART: EChartsOption = {
  backgroundColor: 'transparent',
  labelLayout: { hideOverlap: true },
  animation: true,
  animationDuration: 400,
  animationEasing: 'quarticOut',
  animationDelayUpdate: (idx) => idx * 10,

  stateAnimation: { duration: 200, easing: 'quarticOut' },
  // emphasis: { focus: 'series', blurScope: 'coordinateSystem' },

  // Tipografia global
  textStyle: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji"',
  },

  tooltip: {
    show: true,
    trigger: 'axis',
    appendToBody: true,
    backgroundColor: 'var(--chart-tooltip-bg, var(--current-bg, #fff))',
    borderColor: 'transparent',
    extraCssText:
      'box-shadow: 0 8px 24px rgba(0,0,0,.08); border-radius: 10px; padding: 10px 12px;',
    textStyle: {
      color: 'var(--chart-tooltip-fg, var(--tw-gray-700))',
      fontSize: 12,
    },
    // axisPointer: {
    //   type: 'cross',
    //   crossStyle: { color: 'var(--tw-gray-300,#d1d5db)', width: 1 },
    //   label: {
    //     backgroundColor: 'var(--chart-tooltip-label-bg, #1f2937)',
    //     color: 'var(--chart-tooltip-label-fg, #fff)'
    //   }
    // },
    axisPointer: {
      type: 'shadow',
    },
  },

  color: ['#6366f1', '#22d3ee', '#10b981', '#f59e0b', '#ef4444', '#94a3b8'],

  grid: {
    top: 40,
    left: 2,
    bottom: 0,
    right: 32,
    containLabel: true,
  },

  legend: {
    show: true,
    type: 'scroll',
    top: 0,
    left: 0,
    icon: 'roundRect',
    formatter: (name: string) =>
      name && name.length > 20 ? name.slice(0, 18) + '…' : name,
    tooltip: { show: true },
  },

  xAxis: {
    type: 'value',
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      // color: 'var(--tw-gray-600,#4b5563)',
      fontSize: 11,
      margin: 8,
    },
  },

  yAxis: {
    type: 'value',
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      // color: 'var(--tw-gray-600,#4b5563)',
      fontSize: 11,
      margin: 8,
    },
  },

  // media: [
  //   // telas pequenas
  //   {
  //     query: { maxWidth: 480 },
  //     option: {
  //       legend: { top: 4 },
  //       grid: { top: 40, right: 12, left: 8, bottom: 20 },
  //       xAxis: { axisLabel: { fontSize: 10, margin: 8 } },
  //       yAxis: { axisLabel: { fontSize: 10, margin: 6 } },
  //     }
  //   }
  // ],
};

// export const DEFAULT_DATAZOOM_SCROLLBAR: DataZoomComponentOption =
export const DEFAULT_DATAZOOM_SCROLLBAR: any = {
  type: 'slider',
  yAxisIndex: 0,
  zoomLock: true,
  brushSelect: false,
  width: 8,
  right: 0,

  labelFormatter: function () {
    return ''; // Retorna uma string vazia para ocultar os rótulos
  },
  // handleSize: 0,
  backgroundColor: 'var(--p-navigation-item-focus-background)',
  dataBackground: {
    areaStyle: {
      color: 'transparent',
      shadowColor: 'transparent',
    },
    lineStyle: {
      color: 'transparent',
      shadowColor: 'transparent',
    },
  },

  borderColor: 'transparent',
  fillerColor: 'var(--p-text-color)',

  borderWidth: 0,
  zoomOnMouseWheel: 'shift',
  moveOnMouseWheel: true,
  showDetail: false,
  borderRadius: 999,
  showDataShadow: undefined,
  brushStyle: {
    borderWidth: 0,
    borderColor: 'var(--p-text-color)',
    color: 'var(--p-text-color)',
    borderCap: 'round',
    borderJoin: 'bevel',
    borderRadius: 0,
  },
  handleStyle: {
    borderWidth: 0,
    borderColor: 'var(--p-text-color)',
    color: 'var(--p-text-color)',
    borderCap: 'round',
    borderJoin: 'bevel',
    borderRadius: 0,
  },
  moveHandleStyle: {
    borderWidth: 0,
    borderColor: 'var(--p-text-color)',
    color: 'var(--p-text-color)',
    borderCap: 'round',
    borderJoin: 'bevel',
    borderRadius: 0,
  },
};

export const DEFAULT_DATAZOOM_SCROLLBAR_HORIZONTAL: DataZoomComponentOption = {
  ...DEFAULT_DATAZOOM_SCROLLBAR,
  width: 'auto',
  height: 8,
  left: 16,
  bottom: 4,
  yAxisIndex: undefined,
  xAxisIndex: 0,
};

export const DEFAULT_DATAZOOM_DRAG: DataZoomComponentOption = {
  type: 'inside',
  zoomOnMouseWheel: false,
  moveOnMouseWheel: true,
  // start: 0,
  yAxisIndex: 0,
};

/** @deprecated prefer AXIS_LABEL_NUMBER (or AXIS_LABEL_CURRENCY / AXIS_LABEL_PERCENT) from @shared/formatters */
export const FORMATTER_NUMBER_ABBREVIATION = AXIS_LABEL_NUMBER;

/** @deprecated prefer formatNumber(value, 2) from @shared/formatters */
export const VALUEFORMATTER_NUMBER_2DECIMAL = {
  valueFormatter: (value: any) => formatNumber(value, 2),
};

/** @deprecated prefer formatCurrency(value) from @shared/formatters */
export const VALUEFORMATTER_NUMBER_CURRENCY = {
  valueFormatter: (value: any) => formatCurrency(value),
};

export const FORMATTER_2DAYS_MONTH = {
  formatter: (value: number) => {
    const d = new Date(value);
    const dia = d.toLocaleDateString(undefined, { day: '2-digit' });
    const mes = d.toLocaleDateString(undefined, { month: 'short' });
    // se o ano for diferente do atual, adicionar o ano
    const anoAtual = new Date().getFullYear();
    const ano = d.getFullYear() !== anoAtual ? ` ${d.getFullYear()}` : '';
    return `${dia} ${mes.charAt(0).toUpperCase() + mes.slice(1)}${ano}`; // 15 set
  },
};

const capitalizeShortMonth = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1);

/** Semana ISO 8601: "13–19 Mai" ou "29 Abr–5 Mai" (cross-month). Cube
 *  retorna o início da semana (segunda-feira) — somamos 6 dias para o fim. */
export const FORMATTER_WEEK_RANGE = {
  formatter: (value: number) => {
    const start = new Date(value);
    const end = new Date(value);
    end.setUTCDate(end.getUTCDate() + 6);
    const sameMonth = start.getUTCMonth() === end.getUTCMonth();
    const anoAtual = new Date().getFullYear();
    const yearSuffix =
      end.getUTCFullYear() !== anoAtual ? ` ${end.getUTCFullYear()}` : '';
    const startDay = start.getUTCDate().toString().padStart(2, '0');
    const endDay = end.getUTCDate().toString().padStart(2, '0');
    const endMonth = capitalizeShortMonth(
      end.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }),
    );
    if (sameMonth) {
      return `${startDay}–${endDay} ${endMonth}${yearSuffix}`;
    }
    const startMonth = capitalizeShortMonth(
      start.toLocaleDateString(undefined, {
        month: 'short',
        timeZone: 'UTC',
      }),
    );
    return `${startDay} ${startMonth}–${endDay} ${endMonth}${yearSuffix}`;
  },
};

/** Mês/ano numérico no padrão MM/AA: "06/26". */
export const FORMATTER_MONTH_YEAR_NUMERIC = {
  formatter: (value: number) => {
    const d = new Date(value);
    const mm = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const yy = (d.getUTCFullYear() % 100).toString().padStart(2, '0');
    return `${mm}/${yy}`;
  },
};

export type TimeGranularity = 'day' | 'week' | 'month';

export function formatterForGranularity(g: string | undefined) {
  switch (g) {
    case 'week':
      return FORMATTER_WEEK_RANGE;
    case 'month':
      return FORMATTER_MONTH_YEAR_NUMERIC;
    default:
      return FORMATTER_2DAYS_MONTH;
  }
}

/**
 * Template nativo do ECharts cascading para `xAxis.axisLabel` em
 * `type: 'time'`. O ECharts escolhe automaticamente o template baseado no
 * espaçamento real dos ticks renderizados — em transições de ano/mês, sobe
 * para o nível superior (year → month → day).
 *
 * Diferente de `formatterForGranularity` (que devolve função para o tooltip
 * do LineChart): aqui devolve string/object aceitos nativamente pelo eixo,
 * delegando a inteligência de cascading ao ECharts. Para `week` mantemos
 * `FORMATTER_WEEK_RANGE` custom (calcula início+fim da semana).
 */
export function axisLabelTemplateForGranularity(g: string | undefined) {
  switch (g) {
    case 'week':
      return FORMATTER_WEEK_RANGE;
    case 'month':
      return {
        formatter: {
          year: '{MM}/{yy}',
          month: '{MM}/{yy}',
        },
      };
    default:
      return {
        formatter: {
          year: '{yyyy}',
          month: '{MMM}',
          day: '{d}',
        },
      };
  }
}

/** @deprecated prefer formatPercent(value, 2) from @shared/formatters */
export const VALUEFORMATTER_NUMBER_PERCENT = {
  valueFormatter: (value: any) => formatPercent(value, 2),
};

export const FUNCTION_NUMBER_PERCENT_AND_CURRENCY = (
  value: any,
  total: any,
) => {
  const a = VALUEFORMATTER_NUMBER_PERCENT.valueFormatter(
    (Number(value) / Number(total)) * 100,
  );
  const b = VALUEFORMATTER_NUMBER_CURRENCY.valueFormatter(value);
  return `(${a})  ${b}`;
};

export const FUNCTION_NUMBER_PERCENT_AND_CURRENCY_HTML = (
  value: any,
  total: any,
) => {
  const a = VALUEFORMATTER_NUMBER_PERCENT.valueFormatter(
    (Number(value) / Number(total)) * 100,
  );
  const b = VALUEFORMATTER_NUMBER_CURRENCY.valueFormatter(value);
  return `<b>(${a})</b>  ${b}`;
};

export const FUNCTION_NUMBER_PERCENT_AND_VALUE_HTML = (
  value: any,
  total: any,
) => {
  const a = VALUEFORMATTER_NUMBER_PERCENT.valueFormatter(
    (Number(value) / Number(total)) * 100,
  );
  const b = value;
  return `<b>(${a})</b>  ${b}`;
};

// // https://echarts.apache.org/en/api.html#echartsInstance.showLoading
// export const DEFAULT_LOADINGOPTS = {
//   text: '',
//   color: '#4D64FF',
//   textColor: '#000',
//   maskColor: 'rgba(255, 255, 255, 0.0)',
//   zlevel: 0,

//   // Font size. Available since `v4.8.0`.
//   fontSize: 12,
//   // Show an animated "spinner" or not. Available since `v4.8.0`.
//   showSpinner: true,
//   // Radius of the "spinner". Available since `v4.8.0`.
//   spinnerRadius: 25,
//   // Line width of the "spinner". Available since `v4.8.0`.
//   lineWidth: 5,
//   // Font thick weight. Available since `v5.0.1`.
//   fontWeight: 'normal',
//   // Font style. Available since `v5.0.1`.
//   fontStyle: 'normal',
//   // Font family. Available since `v5.0.1`.
//   fontFamily: 'sans-serif'
// }
